// Tek gorsel secici: link yapistir (gorsel ya da urun sayfasi), Gorsel
// Kutuphanesi'nden sec ya da dosya yukle. Secilen/yuklenen her gorsel
// kutuphaneye de eklenir — baska seceneklerde ve sablonlarda tekrar kullanilir.

import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Images, Link2, Loader2, Upload, X } from "lucide-react";
import { getBackendUrl } from "../../lib/http";
import { AssetLibrary, assetApi } from "./AssetLibrary";

const abs = (u?: string | null) => (!u ? "" : /^(https?:|data:|blob:)/.test(u) ? u : `${getBackendUrl()}${u}`);

export function ImageSourcePicker({ value, onChange, name, kind = "other" }: {
  value: string;
  onChange: (url: string) => void;
  /** Kutuphaneye kaydedilirken kullanilacak ad */
  name?: string;
  kind?: string;
}) {
  const qc = useQueryClient();
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<string[]>([]);
  const [libOpen, setLibOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const saved = (url: string) => {
    onChange(url);
    setLink(""); setCandidates([]); setErr(null);
    void qc.invalidateQueries({ queryKey: ["listing-assets"] });
  };

  async function fromLink(url: string) {
    if (!url.trim()) return;
    setBusy(true); setErr(null);
    try {
      const r = await assetApi.fromLink({ url: url.trim(), name, kind });
      if (r.asset) saved(r.asset.imageUrl);
      else if (r.candidates?.length) setCandidates(r.candidates);
    } catch (e) { setErr(e instanceof Error ? e.message : "Link okunamadı"); }
    finally { setBusy(false); }
  }

  function upload(file?: File) {
    if (!file) return;
    const fr = new FileReader();
    fr.onload = () => void (async () => {
      setBusy(true); setErr(null);
      try {
        const a = await assetApi.create({ name: name || file.name.replace(/\.[^.]+$/, ""), kind, image: String(fr.result) });
        saved(a.imageUrl);
      } catch (e) { setErr(e instanceof Error ? e.message : "Yüklenemedi"); }
      finally { setBusy(false); }
    })();
    fr.readAsDataURL(file);
  }

  return (
    <div className="mt-0.5 space-y-1.5">
      <div className="flex items-start gap-2">
        {value ? (
          <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
            <img src={abs(value)} alt="" className="h-full w-full object-contain" />
            <button type="button" onClick={() => onChange("")} title="Kaldır"
              className="absolute right-0.5 top-0.5 rounded bg-white/90 p-0.5 text-red-500 shadow"><X className="h-3 w-3" /></button>
          </div>
        ) : (
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg border border-dashed border-gray-300 text-center text-[10px] text-gray-400">görsel yok</div>
        )}
        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex gap-1">
            <input value={link} onChange={(e) => setLink(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void fromLink(link); } }}
              placeholder="Görsel ya da ürün sayfası linki yapıştır"
              className="min-w-0 flex-1 rounded-lg border border-gray-200 px-2 py-1.5 text-sm" />
            <button type="button" onClick={() => void fromLink(link)} disabled={busy || !link.trim()}
              className="flex items-center gap-1 rounded-lg border border-gray-200 px-2 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Link2 className="h-3.5 w-3.5" />} Getir
            </button>
          </div>
          <div className="flex flex-wrap gap-1">
            <button type="button" onClick={() => setLibOpen((v) => !v)}
              className={`flex items-center gap-1 rounded-lg border px-2 py-1 text-xs ${libOpen ? "border-blue-400 bg-blue-50 text-blue-700" : "border-gray-200 text-gray-700 hover:bg-gray-50"}`}>
              <Images className="h-3.5 w-3.5" /> Görsel kütüphanesinden seç
            </button>
            <button type="button" onClick={() => fileRef.current?.click()} disabled={busy}
              className="flex items-center gap-1 rounded-lg border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              <Upload className="h-3.5 w-3.5" /> Dosya yükle
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden"
              onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
          </div>
        </div>
      </div>
      {err && <p className="text-[11px] text-red-600">{err}</p>}
      {candidates.length > 0 && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-2">
          <p className="mb-1 text-[11px] text-gray-600">Sayfadaki görseller — birini seç (kütüphaneye kaydedilir):</p>
          <div className="grid max-h-48 grid-cols-5 gap-1 overflow-y-auto">
            {candidates.map((c) => (
              <button type="button" key={c} onClick={() => void fromLink(c)} disabled={busy}
                className="aspect-square overflow-hidden rounded border border-gray-200 bg-white hover:border-blue-400">
                <img src={c} alt="" className="h-full w-full object-contain" loading="lazy" referrerPolicy="no-referrer" />
              </button>
            ))}
          </div>
        </div>
      )}
      {libOpen && (
        <div className="max-h-72 overflow-y-auto rounded-lg border border-gray-200 p-2">
          <AssetLibrary onPick={(a) => { onChange(a.imageUrl); setLibOpen(false); }} pickedUrls={value ? [value] : []} />
        </div>
      )}
    </div>
  );
}
